import { FC, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import { styles } from "./stylesSendZapModal";
import LightningSvg from "@/assets/svg/LightningSvg";
import { useZapMutation } from "@/store/api/walletApi";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { useSelector } from "react-redux";
import RemoveImgArticle from "@/assets/svg/RemoveImgArticle";
import theme from "@/theme";
import { RootState } from "@/store/store";
import { formatMoney } from "@/helpers/formatMoney";

interface Props {
  open: boolean;
  onClose: () => void;
  username: string;
  avatarUrl: string;
  postUid: string;
  onSuccess?: (userAvatar: string) => void;
  refetchBalance: () => void;
  setToast: (value: { message: string; success: boolean } | null) => void;
  setTotalZapsAmountState: React.Dispatch<React.SetStateAction<number>>;
  totalZapsAmountState: number;
}

const usdOptions = [5, 10, 15, 20, 25];

const SendZapModal: FC<Props> = ({
  open,
  onClose,
  username,
  avatarUrl,
  postUid,
  onSuccess,
  refetchBalance,
  setToast,
  setTotalZapsAmountState,
  totalZapsAmountState,
}) => {
  const [zap] = useZapMutation();
  const [selectedUSD, setSelectedUSD] = useState<number | null>(5);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [comment, setComment] = useState("");
  const inputBoxWidth = Math.min(400, Math.max(106, 16 * customAmount.length));
  const inputWidth = Math.min(395, Math.max(103, 16 * customAmount.length));
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const userAvatar = useSelector((state: RootState) => state.auth.user?.image?.url);

  const finalAmount = isOtherSelected ? Number(customAmount) : selectedUSD;
  const isZapDisabled = isOtherSelected && (!customAmount || Number(customAmount) <= 0);

  const handleZap = async () => {
    if (!finalAmount || finalAmount < 1) {
      setToast({ message: "Please enter a valid amount", success: false });
      return;
    }
    setLoading(true);
    try {
      const res = await zap({
        post_id: postUid,
        amount_usdt: finalAmount,
        message: comment || "",
      }).unwrap();
      setTotalZapsAmountState(totalZapsAmountState + res.netAmount);
      refetchBalance();
      onSuccess?.(userAvatar ?? "");
      setToast({ message: "Zap sent successfully!", success: true });
      onClose();
    } catch (error: any) {
      console.error("Zap error", error);
      const message = error?.data?.message || "Failed to send zap";
      setToast({ message, success: false });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9.]/g, "");
    setCustomAmount(numericValue);
  };

  const formatWithDots = (value: string | number) => {
    if (!value) return "";
    return Number(value).toLocaleString("en-US");
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: styles.modal }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.6)",
            zIndex: 10,
          }}
        >
          <CustomLoader size={48} isModal={true} />
        </Box>
      )}

      <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <Avatar src={avatarUrl} sx={{ width: 32, height: 32 }} />
          <Typography sx={styles.headerText}>Send zap to {username}</Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disableRipple
          disableFocusRipple
          sx={{ p: 0, minWidth: 0, minHeight: 0 }}
        >
          <RemoveImgArticle size={24} />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        <Typography sx={styles.label}>Zap amount:</Typography>

        <Box sx={styles.zapGrid}>
          {usdOptions.map((amount) => (
            <Box
              key={amount}
              sx={{
                ...styles.zapItem,
                borderColor:
                  selectedUSD === amount && !isOtherSelected
                    ? theme.palette.custom.mainBlue
                    : "#E4E7E9",
                background: selectedUSD === amount && !isOtherSelected ? "#FFFFFF" : "#F8F9F9",
                color:
                  selectedUSD === amount && !isOtherSelected
                    ? theme.palette.custom.mainBlue
                    : "#2A2D31",
                fontWeight: 600,
                fontSize: "16px",
                gap: "4px",
              }}
              onClick={() => {
                setSelectedUSD(amount);
                setCustomAmount("");
                setIsOtherSelected(false);
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>${amount}</Typography>
            </Box>
          ))}

          {/* Other / custom input */}
          <Box
            sx={{
              ...styles.zapItem,
              ...styles.otherOption,
              borderColor: isOtherSelected ? theme.palette.custom.mainBlue : "#E4E7E9",
              background: "#FFFFFF",
              width: `${inputBoxWidth}px`,
              fontWeight: isOtherSelected ? 700 : 500,
              color: theme.palette.custom.mainBlue,
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TextField
              variant="standard"
              placeholder={isFocused ? "" : "Other"}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              value={formatWithDots(customAmount)}
              onClick={(e) => {
                e.stopPropagation();
                setIsOtherSelected(true);
              }}
              onChange={handleCustomAmountChange}
              inputProps={{
                style: {
                  fontFamily: "Inter",
                  fontWeight: 700,
                  fontSize: "16px",
                  textAlign: "center",
                  color: theme.palette.custom.mainBlue,
                },
              }}
              InputProps={{ disableUnderline: true }}
              sx={{
                width: `${inputWidth}px`,
                padding: "9px 12px",
                height: "40px",
                borderRadius: "32px",
                "& input::placeholder": {
                  color: "#D6D6D6",
                  textAlign: "center",
                },
                "& input": { padding: 0 },
              }}
            />
          </Box>
        </Box>

        <TextField
          placeholder="Comment (Optional)"
          variant="outlined"
          fullWidth
          value={comment}
          onChange={(e) => {
            if (e.target.value.length <= 40) setComment(e.target.value);
          }}
          inputProps={{ maxLength: 40 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: comment.length >= 40 ? "#EF4444" : "#9DA6B0",
                    fontFamily: "Inter",
                  }}
                >
                  {comment.length}/40
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={styles.commentInput}
        />

        <Button
          fullWidth
          variant="outlined"
          sx={{
            ...styles.sendButton,
            ...styles.lightningIconDisabled,
            textTransform: "none",
          }}
          onClick={handleZap}
          disabled={isZapDisabled || loading}
        >
          <LightningSvg
            className="lightning-icon"
            color={isZapDisabled ? "#C6CACE" : theme.palette.custom.mainBlue}
          />
          <Typography
            component="p"
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "18px",
            }}
          >
            Zap ${isZapDisabled ? formatMoney(1) : formatMoney(finalAmount)} USDC
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default SendZapModal;
